import { coordEq, isTransport, unitAt } from "@dailywars/core/engine";
import type { GameState, Unit } from "@dailywars/core/engine";

import { buildUnitSelection, canCaptureAt, canOpenBuildMenu } from "#/game/selection/transitions";
import type { SelectionState } from "#/game/selection/types";
import {
  commitCapture,
  commitJoin,
  commitLoad,
  commitPrepareUnload,
  commitUnload,
  commitWait,
  commitWaitOrMove,
  selectTransportUnload,
  undoLastMove,
} from "#/game/store/gameMutations";
import type { StoreState } from "#/game/store/types";

import type { TileSelectContext } from "./tileSelectContext";
import { selectEnemyInspect } from "./tileSelectNoneInspect";
import {
  confirmAttackFromOptions,
  canShowUnloadCursor,
  openBuildingSelectionAtTile,
  startMovePreview,
  startMovePreviewToAlly,
} from "./tileSelectSharedHelpers";

type MovePreviewSelection = Extract<SelectionState, { mode: "move-preview" }>;
type TransportUnloadSelection = Extract<SelectionState, { mode: "transport-unload" }>;
type BuildingSelection = Extract<SelectionState, { mode: "building" }>;

function handleMovePreviewEnemyBranch(
  ctx: TileSelectContext,
  sel: MovePreviewSelection,
): StoreState | undefined {
  const { state, local, clickedUnit } = ctx;
  if (!clickedUnit || clickedUnit.owner === local.currentPlayer) return undefined;
  const option = sel.attackOptions.find((o) => o.targetId === clickedUnit.id);
  if (!option) return selectEnemyInspect(ctx);
  return confirmAttackFromOptions({
    state,
    unitId: sel.unitId,
    path: sel.path,
    clickedUnit,
    forecast: option.forecast,
  });
}

function tryCommitAllyLoadOrJoin({
  state,
  local,
  sel,
  occupied,
}: {
  state: StoreState;
  local: GameState;
  sel: MovePreviewSelection;
  occupied: Unit | undefined;
}): StoreState | undefined {
  if (!occupied || occupied.owner !== local.currentPlayer) return undefined;
  const load = sel.loadOptions.find((o) => o.targetId === occupied.id);
  if (load) {
    return commitLoad({
      state,
      unitId: sel.unitId,
      transportId: load.targetId,
      path: sel.path,
    });
  }
  const join = sel.joinOptions.find((o) => o.targetId === occupied.id);
  if (!join) return undefined;
  return commitJoin({
    state,
    unitId: sel.unitId,
    targetId: join.targetId,
    path: sel.path,
  });
}

function commitTransportAtDestination({
  state,
  sel,
  unit,
}: {
  state: StoreState;
  sel: MovePreviewSelection;
  unit: Unit;
}): StoreState {
  if (sel.path.length === 0) {
    if (canShowUnloadCursor(unit)) return commitPrepareUnload(state, sel.unitId);
    return commitWaitOrMove({ state, unitId: sel.unitId, path: sel.path });
  }
  const afterMove = commitWaitOrMove({ state, unitId: sel.unitId, path: sel.path });
  const moved = afterMove.localState.units.find((u) => u.id === sel.unitId);
  if (!moved?.cargo) return afterMove;
  if (canShowUnloadCursor(moved)) return commitPrepareUnload(afterMove, sel.unitId);
  if (moved.unloadReady) return selectTransportUnload(afterMove, sel.unitId);
  return afterMove;
}

function handleMovePreviewDestinationCommit(
  ctx: TileSelectContext,
  sel: MovePreviewSelection,
): StoreState {
  const { state, local } = ctx;
  const unit = local.units.find((u) => u.id === sel.unitId);
  if (!unit) return { ...state, selection: { mode: "none" } };
  const allyResult = tryCommitAllyLoadOrJoin({
    state,
    local,
    sel,
    occupied: unitAt(local, sel.destination),
  });
  if (allyResult) return allyResult;
  if (canCaptureAt({ state: local, unit, pos: sel.destination })) {
    return commitCapture({ state, unitId: sel.unitId, path: sel.path });
  }
  if (isTransport(unit.kind) && unit.cargo) {
    return commitTransportAtDestination({ state, sel, unit });
  }
  return commitWaitOrMove({ state, unitId: sel.unitId, path: sel.path });
}

export function handleMovePreviewMode(
  ctx: TileSelectContext,
  sel: MovePreviewSelection,
): StoreState {
  const { state, local, tile, clickedUnit } = ctx;

  const enemyResult = handleMovePreviewEnemyBranch(ctx, sel);
  if (enemyResult !== undefined) return enemyResult;

  if (coordEq(sel.destination, tile)) {
    return handleMovePreviewDestinationCommit(ctx, sel);
  }

  if (sel.reachable.some((c) => coordEq(c, tile))) {
    return startMovePreview({ state, local, sel, tile });
  }

  if (clickedUnit?.owner === local.currentPlayer) {
    const isLoadOrJoin =
      sel.loadOptions.some((o) => o.targetId === clickedUnit.id) ||
      sel.joinOptions.some((o) => o.targetId === clickedUnit.id);
    if (isLoadOrJoin) {
      return startMovePreviewToAlly({ state, local, sel, ally: clickedUnit });
    }
  }

  return { ...state, selection: { mode: "none" }, previewAnimation: null };
}

export function handleTransportUnloadMode(
  ctx: TileSelectContext,
  sel: TransportUnloadSelection,
): StoreState {
  const { state, tile, clickedOwnSelectable } = ctx;

  if (clickedOwnSelectable && clickedOwnSelectable.id === sel.unitId) {
    return commitWait(state, sel.unitId);
  }

  if (sel.unloadTiles.some((c) => coordEq(c, tile))) {
    return commitUnload({ state, unitId: sel.unitId, pos: tile });
  }

  const reverted = undoLastMove(state);

  if (clickedOwnSelectable) {
    const unit = reverted.localState.units.find((u) => u.id === clickedOwnSelectable.id);
    if (unit) return { ...reverted, selection: buildUnitSelection(reverted.localState, unit) };
  }

  return reverted;
}

export function handleBuildingMode(ctx: TileSelectContext, sel: BuildingSelection): StoreState {
  const { state, local, tile, clickedOwnSelectable } = ctx;

  if (coordEq(sel.pos, tile)) return state;
  if (clickedOwnSelectable) {
    return { ...state, selection: buildUnitSelection(local, clickedOwnSelectable) };
  }
  if (canOpenBuildMenu(local, tile)) {
    return openBuildingSelectionAtTile({ state, local, tile });
  }
  return { ...state, selection: { mode: "none" }, previewAnimation: null };
}
